import { Button } from '@cbhq/cds-web/buttons';
import { VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHeader,
  TableRow,
} from '@cbhq/cds-web/tables';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Link, TextBody, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { MAX_MEMBER_INVITE_LIMIT, useProject } from ':cloud/contexts/ProjectContext';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { ProjectRoleNEW, ProjectTeamMember, ProjectWithRole } from ':cloud/types/service_proto';
import { formatRole, getDisplayName } from ':cloud/utils/formatters/naming';
import { ROLES_DOCS_LINK } from ':cloud/utils/links';
import { ActionCell } from ':cloud/widgets/access/components/ActionCellNEW';
import Permission from ':cloud/widgets/auth';
import { OWNER_ADMIN_ROLES } from ':cloud/widgets/auth/util';

import { AccessTableWrapperProps, inviteWarning, isProjectInvite } from './AccessTableWrapperNEW';

export function MobileAccessTableWrapperNEW({
  toggleInviteModalOn,
  handleMemberAction,
}: AccessTableWrapperProps) {
  const { user } = useGetUser();
  // Remove type cast after project based team management is fully rolled out - useIsProjectTeamsEnabled
  const { selectedProject } = useProject() as {
    selectedProject: ProjectWithRole | null;
  };

  const users = selectedProject?.users || [];
  const invites = selectedProject?.invites || [];
  const hasInvitedMaxUsers = invites?.length >= MAX_MEMBER_INVITE_LIMIT;

  const allUsers = [...users, ...invites];

  const accessTableBody = allUsers
    .filter((member: ProjectTeamMember) => member.role !== ProjectRoleNEW.Unspecified)
    .map((member: ProjectTeamMember) => {
      const signedInUser = user.email === member?.email;
      const isInvite = isProjectInvite(member);

      return (
        <TableRow key={`row--${member?.email}`}>
          <TableCell
            start={
              <Avatar
                alt={member.email}
                size="xl"
                selected={signedInUser}
                dangerouslySetSize={34}
                src={isInvite ? undefined : member.avatarUrl}
              />
            }
          >
            <TextHeadline as="p" overflow="break">
              {isInvite ? '' : getDisplayName(member, true)}
            </TextHeadline>
          </TableCell>
          <TableCell direction="horizontal" justifyContent="flex-start">
            <TextHeadline spacingVertical={2} as="p">
              {formatRole(member.role)}
            </TextHeadline>
            {signedInUser ? <Tag colorScheme="blue">YOU</Tag> : null}
          </TableCell>
          <Permission permission={OWNER_ADMIN_ROLES}>
            <ActionCell
              teamMember={member}
              signedInUser={signedInUser}
              handleMemberAction={handleMemberAction}
            />
          </Permission>
        </TableRow>
      );
    });

  return (
    <Table bordered variant="ruled" tableLayout="fixed" compact>
      <TableCaption>
        <VStack spacingTop={1} spacingHorizontal={0.5}>
          <TextTitle3 as="h3">Project team</TextTitle3>
          <VStack gap={2} width="100%" justifyContent="space-between" spacingVertical={2}>
            <TextBody as="p" color="foregroundMuted">
              A list of people who can access this project, including their roles. For details, see{' '}
              <Link href={ROLES_DOCS_LINK} openInNewWindow underline={false}>
                Roles and Permissions
              </Link>
            </TextBody>
            <Permission permission={OWNER_ADMIN_ROLES}>
              <VStack justifyContent="center">
                {user.exchangeEnabled ? (
                  <Tooltip content={inviteWarning} placement="bottom">
                    <Button disabled onPress={toggleInviteModalOn}>
                      Invite member
                    </Button>
                  </Tooltip>
                ) : (
                  <Button disabled={hasInvitedMaxUsers} onPress={toggleInviteModalOn}>
                    Invite member
                  </Button>
                )}
              </VStack>
            </Permission>
          </VStack>
        </VStack>
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableCell title="Name" width="45%" />
          <TableCell title="Role" />
          <Permission permission={OWNER_ADMIN_ROLES}>
            <TableCell title="Actions" alignItems="flex-end" />
          </Permission>
        </TableRow>
      </TableHeader>
      <TableBody>{accessTableBody}</TableBody>
    </Table>
  );
}
